// components/header/TopBar.tsx
'use client';

import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FiSearch, FiShoppingCart, FiUser, FiChevronDown, FiLogOut, FiX, FiGift } from 'react-icons/fi';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DeliveryAddressDisplay } from './DeliveryAddressDisplay';

const HeaderSearchAutocomplete = dynamic(
  () => import('./HeaderSearchAutocomplete').then(mod => mod.HeaderSearchAutocomplete),
  {
    loading: () => (
      <div className="absolute top-full mt-2 left-1/2 z-50 flex w-[clamp(100%,600px,90vw)] -translate-x-1/2 items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#1f2937]/95 px-4 py-6 shadow-2xl backdrop-blur-xl">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#febd69]/40 border-t-[#febd69]" />
        <span className="text-sm text-gray-400">Searching...</span>
      </div>
    ),
  }
);

// 사용자 메뉴 컴포넌트 분리
const UserMenu = memo(({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { user, login, logout } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // 로그인 핸들러
  const handleLogin = async () => {
    try {
      console.log('Login button clicked, attempting to login...');
      await login();
      console.log('Login function completed successfully');
      onClose();
    } catch (error) {
      console.error('Login failed:', error);
      // 에러가 발생해도 메뉴는 닫기
      onClose();
    }
  };

  // 로그아웃 핸들러
  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Logout failed:', error);
      // 에러가 발생해도 메뉴는 닫기
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef}
      className="absolute right-0 mt-2 w-56 bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-xl shadow-2xl py-1 z-50 border border-white/10 overflow-hidden"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="user-menu-button"
    >
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#722f37]/10 via-transparent to-[#800020]/10 pointer-events-none" />
      
      {user ? (
        <>
          <div className="relative px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {user.email.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-white text-sm truncate">{user.email.split('@')[0]}</div>
                <div className="text-xs text-gray-400 truncate">{user.email}</div>
              </div>
            </div>
          </div>
          
          <div className="py-1">
            <button
              onClick={() => {
                router.push('/account');
                onClose();
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-white/10 flex items-center gap-2.5 transition-all duration-200 group"
              role="menuitem"
            >
              <div className="w-7 h-7 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <FiUser className="w-3.5 h-3.5" />
              </div>
              <span>My Profile</span>
              <FiChevronDown className="w-3.5 h-3.5 ml-auto -rotate-90 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>
            
            <button
              onClick={() => {
                router.push('/account?section=orders');
                onClose();
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-white/10 flex items-center gap-2.5 transition-all duration-200 group"
              role="menuitem"
            >
              <div className="w-7 h-7 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <FiShoppingCart className="w-3.5 h-3.5" />
              </div>
              <span>Order History</span>
              <FiChevronDown className="w-3.5 h-3.5 ml-auto -rotate-90 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>

            <button
              onClick={() => {
                router.push('/account?section=rewards');
                onClose();
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-white/10 flex items-center gap-2.5 transition-all duration-200 group"
              role="menuitem"
            >
              <div className="w-7 h-7 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <FiGift className="w-3.5 h-3.5" />
              </div>
              <span>My Rewards</span>
              <FiChevronDown className="w-3.5 h-3.5 ml-auto -rotate-90 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>
          
          <div className="border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 flex items-center gap-2.5 transition-all duration-200 group"
              role="menuitem"
            >
              <div className="w-7 h-7 bg-red-500/10 rounded-lg flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                <FiLogOut className="w-3.5 h-3.5" />
              </div>
              <span>Sign out</span>
            </button>
          </div>
        </>
      ) : (
        <div className="py-1">
          <button
            onClick={handleLogin}
            className="w-full px-4 py-3 text-left hover:bg-white/10 transition-all duration-200 group"
            role="menuitem"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-[#722f37] to-[#800020] rounded-full flex items-center justify-center text-white">
                <FiUser className="w-4 h-4" />
              </div>
              <div>
                <div className="text-white font-medium text-sm">Welcome!</div>
                <div className="text-xs text-gray-400">Sign in to your account</div>
              </div>
              <FiChevronDown className="w-3.5 h-3.5 ml-auto -rotate-90 text-gray-400 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </div>
          </button>
        </div>
      )}
    </div>
  );
});

UserMenu.displayName = 'UserMenu';

const TopBar = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // CSS-hidden autocomplete instances still fetch. Mount only the visible one.
  const isDesktopViewport = useMediaQuery('(min-width: 768px)');
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const { items, toggleCart } = useCart();
  const { user, isLoading, isSignInOpen, login } = useAuth();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  // 클라이언트 사이드 마운트 확인
  // The flag must flip in the same passive-effect batch as AuthProvider's cookie
  // restore, so the header never paints a signed-out frame for a signed-in
  // customer. useSyncExternalStore would re-render in a separate sync pass first.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- must batch with AuthProvider's restore (see above)
    setMounted(true);
  }, []);

  // Click outside to close search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideDesktop = desktopSearchRef.current?.contains(target);
      const isInsideMobile = mobileSearchRef.current?.contains(target);
      if (!isInsideDesktop && !isInsideMobile) {
        closeSearch();
      }
    };
    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen, closeSearch]);

  // Escape key to close search dropdown
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSearch();
    };
    if (isSearchOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSearchOpen, closeSearch]);

  const handleSearchInput = useCallback((value: string) => {
    setSearchQuery(value);
    setIsSearchOpen(value.trim().length >= 2);
  }, []);

  const handleSearchFocus = useCallback(() => {
    if (searchQuery.trim().length >= 2) {
      setIsSearchOpen(true);
    }
  }, [searchQuery]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setIsSearchOpen(false);
  }, []);

  // 검색 제출 — 추적은 검색 결과 페이지의 SearchTracker가 results settle 후 처리
  // (autocomplete의 totalFound는 debounce/in-flight 상태와 race가 있어 0으로 잘못 잡힘)
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/collections/typesense?q=${encodeURIComponent(searchQuery.trim())}`);
      clearSearch();
    }
  }, [searchQuery, router, clearSearch]);

  return (
    <div className="bg-[#131921] text-white">
      {/* 첫 번째 줄: 로고, 사용자 메뉴, 장바구니 */}
      <div className="w-full max-w-[1280px] mx-auto px-2 sm:px-4 lg:px-6 flex items-center justify-between gap-2 py-2">
        {/* Logo - 모바일에서 더 작게 */}
        <Link 
          href="/" 
          className="flex items-center gap-1.5 md:gap-2 group flex-shrink-0"
        >
          <div className="relative w-7 h-7 md:w-8 md:h-8">
            <div className="absolute inset-0 bg-gradient-to-br from-[#febd69] to-[#f3a847] rounded-lg transform rotate-45 group-hover:rotate-90 transition-transform duration-500" />
            <div className="absolute inset-0.5 bg-[#131921] rounded-lg flex items-center justify-center">
              <span className="text-[#febd69] font-bold text-base md:text-lg">A</span>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Amsterwine
            </span>
            <span className="hidden md:block text-[12px] text-[#febd69] font-medium tracking-wider lowercase">
              wine & spirits company
            </span>
          </div>
        </Link>

        {/* Delivery Location - 데스크톱에서만 표시 (ZIP 기반 dynamic) */}
        <div className="hidden md:flex">
          <DeliveryAddressDisplay />
        </div>

        {/* 데스크톱 검색 바 */}
        <div className="hidden md:block flex-1 mx-4">
          <div className="relative" ref={desktopSearchRef}>
            <form onSubmit={handleSearch} className="flex items-center bg-white/10 backdrop-blur-xs rounded-full px-2 py-1.5 border border-white/20">
              <FiSearch className="w-4 h-4 text-gray-400 ml-2" />
              <label htmlFor="main-desktop-search" className="sr-only">Search products</label>
              <input
                type="text"
                id="main-desktop-search"
                name="search"
                placeholder="Search wines..."
                className="w-full bg-transparent px-2 py-1 text-white placeholder-gray-400 outline-hidden focus:ring-0 border-0 text-base md:text-sm"
                aria-label="Search products"
                value={searchQuery}
                onChange={e => handleSearchInput(e.target.value)}
                onFocus={handleSearchFocus}
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-gray-400 hover:text-white transition-colors duration-200 p-1"
                >
                  <FiX className="w-4 h-4" />
                </button>
              )}
              <div className="h-4 w-px bg-white/20 mx-1" />
              <button
                type="submit"
                className="px-4 py-1.5 text-sm font-medium text-white bg-[#febd69]/30 hover:bg-[#febd69]/40
                  rounded-full transition-all duration-200 flex items-center gap-1.5 group"
                aria-label="Submit search"
              >
                <span>Search</span>
                <FiSearch className="w-3.5 h-3.5 text-[#febd69] group-hover:translate-x-0.5 transition-transform" />
              </button>
            </form>
            {isSearchOpen && isDesktopViewport && (
              <HeaderSearchAutocomplete
                query={searchQuery}
                onClose={closeSearch}
              />
            )}
          </div>
        </div>

        {/* User Menu - 데스크톱에서만 표시 */}
        <div className="hidden md:block relative">
          <button
            onClick={() => user ? setIsUserMenuOpen(!isUserMenuOpen) : void login()}
            className="flex items-center gap-2 text-white hover:text-orange-300 transition-all duration-300 justify-end
              bg-gradient-to-r from-white/5 to-white/10 hover:from-white/10 hover:to-white/20 
              rounded-full px-3 md:px-4 py-1.5 md:py-2 backdrop-blur-xs border border-white/10 hover:border-white/20
              shadow-[0_2px_10px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
            aria-expanded={user ? isUserMenuOpen : isSignInOpen}
            aria-haspopup={mounted && user ? 'menu' : 'dialog'}
            aria-label={mounted && user ? 'User menu' : 'Sign in'}
            id="user-menu-button"
          >
            <div className="relative">
              <FiUser className="w-4 h-4 md:w-5 md:h-5" />
              <div className="absolute -inset-1 bg-orange-300/20 rounded-full blur-xs opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="flex items-center text-xs leading-tight text-right">
              {!mounted ? (
                <span className="text-xs md:text-sm font-medium">Sign in</span>
              ) : isLoading ? (
                <span className="text-xs md:text-sm font-medium">Loading...</span>
              ) : user ? (
                <span className="text-orange-300 font-medium text-xs md:text-sm">
                  Hi, {user.firstName || (user.email.split('@')[0].charAt(0).toUpperCase() + user.email.split('@')[0].slice(1))}!
                </span>
              ) : (
                <span className="text-xs md:text-sm font-medium">Sign in</span>
              )}
            </div>
            <FiChevronDown className="w-3 h-3 md:w-4 md:h-4 transition-transform duration-300 group-hover:rotate-180" />
          </button>

          <UserMenu isOpen={isUserMenuOpen} onClose={() => setIsUserMenuOpen(false)} />
        </div>

        {/* 모바일: 배송 주소 + Sign-in + Cart 버튼 그룹 */}
        <div className="md:hidden flex items-center gap-1.5">
          {/* 배송 주소 — sign-in 옆 compact pill */}
          <DeliveryAddressDisplay variant="compact" />

          {/* User Menu - 모바일 (로그인 시 first initial avatar, 비로그인 시 user icon) */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => user ? setIsMobileUserMenuOpen(!isMobileUserMenuOpen) : void login()}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                mounted && user
                  ? 'bg-[#722f37] text-white hover:bg-[#5e2730] border border-white/10'
                  : 'text-white hover:text-orange-300 bg-white/5 hover:bg-white/10 backdrop-blur-xs border border-white/10 hover:border-white/20'
              }`}
              aria-expanded={user ? isMobileUserMenuOpen : isSignInOpen}
              aria-haspopup={mounted && user ? 'menu' : 'dialog'}
              aria-label={mounted && user ? `Account: ${user.firstName ?? user.email.split('@')[0]}` : 'Sign in'}
            >
              {mounted && user ? (
                <span className="text-sm font-semibold leading-none">
                  {(user.firstName?.[0] ?? user.email[0]).toUpperCase()}
                </span>
              ) : (
                <FiUser className="w-[18px] h-[18px]" />
              )}
            </button>

            <UserMenu isOpen={isMobileUserMenuOpen} onClose={() => setIsMobileUserMenuOpen(false)} />
          </div>

          {/* Cart Button - 모바일 (icon only, badge로 수량 표시) */}
          <button
            onClick={toggleCart}
            className="relative flex items-center justify-center text-white hover:text-orange-300 transition-all duration-300
              bg-gradient-to-r from-white/5 to-white/10 hover:from-white/10 hover:to-white/20
              rounded-full backdrop-blur-xs border border-white/10 hover:border-white/20
              shadow-[0_2px_10px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.15)] group
              w-10 h-10"
            aria-label={mounted && totalItems > 0 ? `Shopping cart, ${totalItems} items` : 'Shopping cart'}
          >
            <div className="relative">
              <FiShoppingCart className="w-[18px] h-[18px]" />
              <div className="absolute -inset-1 bg-orange-300/20 rounded-full blur-xs opacity-0 group-hover:opacity-100 transition-opacity" />
              {mounted && totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-[#febd69] to-[#f3a847] text-white text-[9px] font-bold
                  rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform
                  border border-[#131921]">
                  {totalItems}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* 데스크톱 Cart Button */}
        <button
          onClick={toggleCart}
          className="hidden md:flex items-center gap-2 text-white hover:text-orange-300 transition-all duration-300
            bg-gradient-to-r from-white/5 to-white/10 hover:from-white/10 hover:to-white/20 
            rounded-full px-4 py-2 backdrop-blur-xs border border-white/10 hover:border-white/20
            shadow-[0_2px_10px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.15)] group"
          aria-label="Shopping cart"
        >
          <div className="relative">
            <FiShoppingCart className="w-5 h-5" />
            <div className="absolute -inset-1 bg-orange-300/20 rounded-full blur-xs opacity-0 group-hover:opacity-100 transition-opacity" />
            {mounted && totalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-[#febd69] to-[#f3a847] text-white text-xs font-bold 
                rounded-full w-5 h-5 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform
                border border-white/20">
                {totalItems}
              </span>
            )}
          </div>
          <span className="text-sm font-medium">Cart</span>
        </button>
      </div>

      {/* 두 번째 줄: 모바일 검색 (배송 주소는 첫 줄로 이동) */}
      <div className="md:hidden pb-2 w-full max-w-[1280px] mx-auto px-2 sm:px-4 lg:px-6">
        {/* 검색 기능 */}
        <div className="relative" ref={mobileSearchRef}>
          <form onSubmit={handleSearch} className="flex items-center bg-white/10 backdrop-blur-xs rounded-full px-2 py-1.5 border border-white/20">
            <FiSearch className="w-4 h-4 text-gray-400 ml-2" />
            <label htmlFor="main-mobile-search" className="sr-only">Search products</label>
            <input
              type="text"
              id="main-mobile-search"
              name="search"
              placeholder="Search wines..."
              className="w-full bg-transparent px-2 py-1 text-white placeholder-gray-400 outline-hidden focus:ring-0 border-0 text-base"
              aria-label="Search products"
              value={searchQuery}
              onChange={e => handleSearchInput(e.target.value)}
              onFocus={handleSearchFocus}
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="text-gray-400 hover:text-white transition-colors duration-200 p-1"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
            <div className="h-4 w-px bg-white/20 mx-1" />
            <button
              type="submit"
              className="px-4 py-1.5 text-sm font-medium text-white bg-[#febd69]/30 hover:bg-[#febd69]/40
                rounded-full transition-all duration-200 flex items-center gap-1.5 group"
              aria-label="Submit search"
            >
              <span>Search</span>
              <FiSearch className="w-3.5 h-3.5 text-[#febd69] group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>
          {isSearchOpen && !isDesktopViewport && (
            <HeaderSearchAutocomplete
              query={searchQuery}
              onClose={closeSearch}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TopBar);
